function normalizeOccupancyMode(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (
    normalized === "staff_technician" ||
    normalized === "independent_owner" ||
    normalized === "renter_guest"
  ) {
    return normalized;
  }
  return "independent_owner";
}

function normalizeClientOwnershipMode(value, occupancyMode) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "salon_owned" || normalized === "independent_owned") {
    return normalized;
  }
  return occupancyMode === "independent_owner" ? "independent_owned" : "salon_owned";
}

function addIf(target, enabled, message) {
  if (enabled) {
    target.push(message);
  }
}

export function buildWorkspaceOperationalBoundary({
  occupancyMode,
  clientOwnershipMode,
  complianceProfileSummary = {},
  ownershipRulesSummary = {},
  warningStateLevel = "none",
  warningTypes = [],
} = {}) {
  const resolvedOccupancyMode = normalizeOccupancyMode(occupancyMode);
  const resolvedClientOwnershipMode = normalizeClientOwnershipMode(
    clientOwnershipMode,
    resolvedOccupancyMode
  );

  const hostControls = [];
  const renterControls = [];

  const priceControl = String(complianceProfileSummary?.price_control || "")
    .trim()
    .toLowerCase();
  const scheduleControl = String(complianceProfileSummary?.schedule_control || "")
    .trim()
    .toLowerCase();
  const productProvider = String(complianceProfileSummary?.product_provider || "")
    .trim()
    .toLowerCase();
  let paymentCollector = String(ownershipRulesSummary?.payment_collector || "")
    .trim()
    .toLowerCase();
  if (!["salon", "renter", "shared"].includes(paymentCollector)) {
    paymentCollector = complianceProfileSummary?.collects_own_payments === false ? "salon" : "renter";
  }

  addIf(hostControls, priceControl === "salon", "Host salon sets service pricing.");
  addIf(renterControls, !priceControl || priceControl === "renter", "Renter sets service pricing.");
  addIf(hostControls, scheduleControl === "salon", "Host salon controls the working schedule.");
  addIf(
    renterControls,
    !scheduleControl || scheduleControl === "renter",
    "Renter controls the working schedule."
  );
  addIf(hostControls, productProvider === "salon", "Host salon provides the operating products.");
  addIf(
    renterControls,
    !productProvider || productProvider === "renter",
    "Renter provides the operating products."
  );
  addIf(hostControls, paymentCollector === "salon", "Host salon collects client-facing payments.");
  addIf(renterControls, paymentCollector === "renter", "Renter collects client-facing payments.");

  let crmScope = "salon_owned";
  let crmScopeLabel = "Salon-owned CRM access";
  let reportingScope = "salon_reporting";
  let reportingScopeLabel = "Booking, CRM, and payout reporting stays with the host salon.";

  if (resolvedClientOwnershipMode === "salon_owned") {
    hostControls.push("Host salon owns the CRM/client relationship for this path.");
  } else {
    renterControls.push("Renter owns the CRM/client relationship for this path.");
    crmScope = "independent_owned";
    crmScopeLabel = "Independent-owned CRM access";
    reportingScope = "independent_reporting";
    reportingScopeLabel =
      "Independent-owned activity should stay out of host-salon CRM and reporting by default.";
  }

  if (resolvedOccupancyMode === "staff_technician") {
    return {
      occupancyMode: resolvedOccupancyMode,
      clientOwnershipMode: resolvedClientOwnershipMode,
      relationshipLabel: "Operate under host salon",
      relationshipDescription:
        "This path is treated as host-salon operational activity rather than an independent rental workflow.",
      independentRenter: false,
      staffWorkflowsApplyByDefault: true,
      crmScope,
      crmScopeLabel,
      reportingScope,
      reportingScopeLabel,
      bookingsScopeLabel: "Bookings belong to the host salon operating workflow.",
      schedulingScopeLabel: "Host salon staff scheduling controls apply.",
      agreementScopeLabel: "Host-salon agreement and payout terms control this path.",
      hostControls,
      renterControls,
      warningStateLevel: String(warningStateLevel || "none").toLowerCase(),
      warningTypes,
    };
  }

  if (resolvedOccupancyMode === "renter_guest") {
    return {
      occupancyMode: resolvedOccupancyMode,
      clientOwnershipMode: resolvedClientOwnershipMode,
      relationshipLabel: "Legacy guest path",
      relationshipDescription:
        "This path is deprecated for launch and should be treated as a historical marketplace guest record rather than a new operating option.",
      independentRenter: false,
      staffWorkflowsApplyByDefault: false,
      crmScope,
      crmScopeLabel,
      reportingScope,
      reportingScopeLabel,
      bookingsScopeLabel: "Legacy guest requests remain readable, but new launch flows stay in the independent-operator lane.",
      schedulingScopeLabel: "Host salon staff scheduling controls should not apply by default.",
      agreementScopeLabel: "Agreement and payment terms should be acknowledged explicitly.",
      hostControls,
      renterControls,
      warningStateLevel: String(warningStateLevel || "none").toLowerCase(),
      warningTypes,
    };
  }

  return {
    occupancyMode: resolvedOccupancyMode,
    clientOwnershipMode: resolvedClientOwnershipMode,
    relationshipLabel: "Operate independently",
    relationshipDescription:
      "This path is an independent renter workflow and should not silently inherit employee or staff operations.",
    independentRenter: true,
    staffWorkflowsApplyByDefault: false,
    crmScope,
    crmScopeLabel,
    reportingScope,
    reportingScopeLabel,
    bookingsScopeLabel:
      "Workspace booking, agreement, and finance records stay auditable, but employee workflows should remain separate.",
    schedulingScopeLabel:
      "Host salon staff scheduling controls should not apply by default to an independent renter.",
    agreementScopeLabel:
      "Independent operating terms, finance terms, and agreement snapshots should be explicit.",
    hostControls,
    renterControls,
    warningStateLevel: String(warningStateLevel || "none").toLowerCase(),
    warningTypes,
  };
}

export function getEntityOperationalBoundary(entity = {}) {
  const workspaceCompliance = entity?.invoice_metadata?.workspace_compliance
    || entity?.pricing_snapshot_json?.workspace_compliance
    || {};
  const acceptanceSnapshot = workspaceCompliance?.acceptance_snapshot || {};
  return buildWorkspaceOperationalBoundary({
    occupancyMode: entity?.occupancy_mode || acceptanceSnapshot?.occupancy_mode,
    clientOwnershipMode:
      entity?.client_ownership_mode || acceptanceSnapshot?.client_ownership_mode,
    complianceProfileSummary:
      acceptanceSnapshot?.compliance_profile_summary
      || workspaceCompliance?.compliance_profile_summary
      || {},
    ownershipRulesSummary:
      acceptanceSnapshot?.ownership_rules_summary
      || workspaceCompliance?.ownership_rules_summary
      || {},
    warningStateLevel:
      acceptanceSnapshot?.warning_state_level || workspaceCompliance?.warning_state_level,
    warningTypes:
      acceptanceSnapshot?.warning_types
      || workspaceCompliance?.warning_types
      || (acceptanceSnapshot?.warnings || [])
        .map((warning) => warning?.warning_type)
        .filter(Boolean),
  });
}
